// STARWEAVE — first-run onboarding + persistent control hints.
//
// A self-contained DOM overlay, themed to index.html (amber #ffb340, condensed
// font, dark glass, vignette). No three.js, no other-module dependencies — just
// the document. Call init() once, after bootDone().
//
//   * First run only (localStorage 'sw_seen_intro' unset): an elegant intro
//     card — title, one line of fiction, the three controls, and a BEGIN button
//     that fades it out and sets the flag.
//   * A subtle persistent control-hint chip in the bottom-right corner that the
//     player can collapse/expand; its state persists in localStorage.
//
// Everything injects its own scoped <style> (ids/classes are 'sw-ob-*' to avoid
// colliding with the HUD), so it can be dropped in without touching index.html.

const SEEN_KEY = 'sw_seen_intro';
const CHIP_KEY = 'sw_hint_collapsed';
const STYLE_ID = 'sw-ob-style';
const ROOT_ID = 'sw-ob-root';

let mounted = false;

// The three real controls, mirrored from flight.ts (drag = steer, double-tap /
// H = hyperdrive). "SCAN" is the evocative framing for approaching a world.
const CONTROLS: { key: string; label: string }[] = [
  { key: 'DRAG', label: 'steer your ship through the dark' },
  { key: 'DOUBLE-TAP · H', label: 'engage the hyperdrive to a new star' },
  { key: 'APPROACH', label: 'fall toward a world to chart it' },
];

function injectStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    #${ROOT_ID} {
      position: fixed;
      inset: 0;
      z-index: 60;
      pointer-events: none;
      font-family: 'Avenir Next Condensed', 'SF Pro Display', system-ui, sans-serif;
      color: var(--ink, #eaf4ff);
    }

    /* ---- First-run intro ------------------------------------------------ */
    .sw-ob-scrim {
      position: absolute;
      inset: 0;
      pointer-events: auto;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 24px;
      background:
        radial-gradient(130% 100% at 50% 32%, rgba(4, 8, 16, 0.55) 0%, rgba(2, 4, 9, 0.86) 70%, rgba(2, 4, 9, 0.94) 100%);
      -webkit-backdrop-filter: blur(3px);
      backdrop-filter: blur(3px);
      opacity: 0;
      transition: opacity 0.85s ease;
    }
    .sw-ob-scrim.in { opacity: 1; }
    .sw-ob-scrim.out { opacity: 0; transition: opacity 0.6s ease; }

    .sw-ob-card {
      position: relative;
      width: min(440px, 92vw);
      padding: 30px 30px 26px;
      background: linear-gradient(180deg, rgba(9, 16, 28, 0.62), rgba(7, 12, 22, 0.82));
      border-left: 2px solid var(--accent, #ffb340);
      -webkit-backdrop-filter: blur(10px);
      backdrop-filter: blur(10px);
      box-shadow: 0 26px 70px rgba(0, 0, 0, 0.55), inset 0 0 60px rgba(120, 200, 255, 0.04);
      clip-path: polygon(0 0, 100% 0, 100% calc(100% - 18px), calc(100% - 18px) 100%, 0 100%);
      transform: translateY(14px) scale(0.985);
      transition: transform 0.85s cubic-bezier(0.16, 0.84, 0.3, 1);
    }
    .sw-ob-scrim.in .sw-ob-card { transform: translateY(0) scale(1); }

    .sw-ob-eyebrow {
      font-size: 10.5px;
      letter-spacing: 4px;
      text-transform: uppercase;
      opacity: 0.55;
      margin-bottom: 12px;
    }
    .sw-ob-title {
      font-size: 30px;
      font-weight: 600;
      letter-spacing: 8px;
      text-transform: uppercase;
      text-shadow: 0 0 18px rgba(120, 200, 255, 0.5);
      margin: 0;
    }
    .sw-ob-title::after {
      content: '';
      display: block;
      width: 64px;
      height: 2px;
      margin-top: 12px;
      background: linear-gradient(90deg, var(--accent, #ffb340), transparent);
      box-shadow: 0 0 10px var(--accent, #ffb340);
    }
    .sw-ob-fiction {
      margin: 18px 0 22px;
      font-size: 15px;
      line-height: 1.55;
      letter-spacing: 0.4px;
      color: var(--accent-soft, #ffd98a);
      opacity: 0.92;
    }

    .sw-ob-controls {
      list-style: none;
      margin: 0 0 26px;
      padding: 16px 0 4px;
      border-top: 1px solid rgba(255, 255, 255, 0.08);
    }
    .sw-ob-controls li {
      display: flex;
      align-items: baseline;
      gap: 14px;
      padding: 7px 0;
    }
    .sw-ob-controls .sw-ob-k {
      flex: 0 0 auto;
      min-width: 116px;
      font-size: 11px;
      letter-spacing: 2px;
      text-transform: uppercase;
      color: var(--accent, #ffb340);
      text-shadow: 0 0 8px rgba(255, 179, 64, 0.35);
    }
    .sw-ob-controls .sw-ob-d {
      font-size: 12.5px;
      letter-spacing: 0.6px;
      opacity: 0.7;
    }

    .sw-ob-begin {
      pointer-events: auto;
      display: inline-flex;
      align-items: center;
      gap: 12px;
      padding: 12px 28px 12px 24px;
      font-family: inherit;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 4px;
      text-transform: uppercase;
      color: #04060c;
      background: linear-gradient(180deg, var(--accent-soft, #ffd98a), var(--accent, #ffb340));
      border: none;
      cursor: pointer;
      clip-path: polygon(0 0, 100% 0, 100% calc(100% - 11px), calc(100% - 11px) 100%, 0 100%);
      box-shadow: 0 8px 26px rgba(255, 179, 64, 0.22);
      transition: filter 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
    }
    .sw-ob-begin:hover { filter: brightness(1.08); box-shadow: 0 10px 32px rgba(255, 179, 64, 0.32); }
    .sw-ob-begin:active { transform: translateY(1px); }
    .sw-ob-begin .sw-ob-arrow {
      font-weight: 400;
      letter-spacing: 0;
      transform: translateX(0);
      transition: transform 0.18s ease;
    }
    .sw-ob-begin:hover .sw-ob-arrow { transform: translateX(4px); }

    /* ---- Persistent corner hint chip ------------------------------------ */
    .sw-ob-chip {
      position: absolute;
      right: calc(env(safe-area-inset-right, 0px) + 18px);
      bottom: calc(env(safe-area-inset-bottom, 0px) + 18px);
      pointer-events: auto;
      max-width: 78vw;
      background: linear-gradient(180deg, rgba(9, 16, 28, 0.34), rgba(9, 16, 28, 0.58));
      border-left: 2px solid var(--accent, #ffb340);
      -webkit-backdrop-filter: blur(7px);
      backdrop-filter: blur(7px);
      clip-path: polygon(0 0, 100% 0, 100% calc(100% - 11px), calc(100% - 11px) 100%, 0 100%);
      opacity: 0;
      transform: translateY(8px);
      transition: opacity 0.5s ease, transform 0.5s ease;
    }
    .sw-ob-chip.in { opacity: 1; transform: translateY(0); }

    .sw-ob-chip-head {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 9px 13px 9px 12px;
      cursor: pointer;
    }
    .sw-ob-chip-dot {
      width: 5px; height: 5px; border-radius: 50%;
      background: var(--accent, #ffb340);
      box-shadow: 0 0 7px var(--accent, #ffb340);
      flex: 0 0 auto;
    }
    .sw-ob-chip-label {
      font-size: 10px;
      letter-spacing: 2.5px;
      text-transform: uppercase;
      opacity: 0.62;
      white-space: nowrap;
    }
    .sw-ob-chip-caret {
      margin-left: auto;
      font-size: 11px;
      line-height: 1;
      opacity: 0.5;
      transition: transform 0.25s ease;
    }
    .sw-ob-chip.collapsed .sw-ob-chip-caret { transform: rotate(180deg); }

    .sw-ob-chip-body {
      overflow: hidden;
      max-height: 160px;
      transition: max-height 0.32s ease, opacity 0.25s ease, padding 0.32s ease;
      padding: 0 13px 11px 12px;
      opacity: 1;
    }
    .sw-ob-chip.collapsed .sw-ob-chip-body {
      max-height: 0;
      opacity: 0;
      padding-top: 0;
      padding-bottom: 0;
    }
    .sw-ob-chip-row {
      display: flex;
      align-items: baseline;
      gap: 9px;
      padding: 2.5px 0;
    }
    .sw-ob-chip-row .sw-ob-ck {
      flex: 0 0 auto;
      min-width: 96px;
      font-size: 9.5px;
      letter-spacing: 1.5px;
      text-transform: uppercase;
      color: var(--accent-soft, #ffd98a);
    }
    .sw-ob-chip-row .sw-ob-cd {
      font-size: 10px;
      letter-spacing: 0.4px;
      opacity: 0.55;
    }

    @media (prefers-reduced-motion: reduce) {
      #${ROOT_ID} *, .sw-ob-card { transition-duration: 0.01ms !important; }
    }
  `;
  document.head.appendChild(s);
}

function ensureRoot(): HTMLElement {
  let root = document.getElementById(ROOT_ID);
  if (!root) {
    root = document.createElement('div');
    root.id = ROOT_ID;
    document.body.appendChild(root);
  }
  return root;
}

function buildChip(root: HTMLElement): void {
  const collapsed = localStorage.getItem(CHIP_KEY) === '1';
  const chip = document.createElement('div');
  chip.className = 'sw-ob-chip' + (collapsed ? ' collapsed' : '');

  const rows = CONTROLS
    .map((c) => `<div class="sw-ob-chip-row"><span class="sw-ob-ck">${c.key}</span><span class="sw-ob-cd">${c.label}</span></div>`)
    .join('');

  chip.innerHTML =
    `<div class="sw-ob-chip-head" role="button" tabindex="0" aria-label="Toggle controls">` +
      `<span class="sw-ob-chip-dot"></span>` +
      `<span class="sw-ob-chip-label">Controls</span>` +
      `<span class="sw-ob-chip-caret">▾</span>` +
    `</div>` +
    `<div class="sw-ob-chip-body">${rows}</div>`;

  const toggle = (): void => {
    const isCollapsed = chip.classList.toggle('collapsed');
    localStorage.setItem(CHIP_KEY, isCollapsed ? '1' : '0');
  };
  const head = chip.querySelector('.sw-ob-chip-head') as HTMLElement;
  head.addEventListener('click', toggle);
  head.addEventListener('keydown', (e) => {
    const k = (e as KeyboardEvent).key;
    if (k === 'Enter' || k === ' ') { e.preventDefault(); toggle(); }
  });

  root.appendChild(chip);
  // Fade in on the next frame so the transition runs.
  requestAnimationFrame(() => requestAnimationFrame(() => chip.classList.add('in')));
}

function buildIntro(root: HTMLElement, onDone: () => void): void {
  const scrim = document.createElement('div');
  scrim.className = 'sw-ob-scrim';

  const controls = CONTROLS
    .map((c) => `<li><span class="sw-ob-k">${c.key}</span><span class="sw-ob-d">${c.label}</span></li>`)
    .join('');

  scrim.innerHTML =
    `<div class="sw-ob-card" role="dialog" aria-modal="true" aria-label="Welcome to Starweave">` +
      `<div class="sw-ob-eyebrow">Cartographer's Log · Entry One</div>` +
      `<h1 class="sw-ob-title">Starweave</h1>` +
      `<p class="sw-ob-fiction">No one has charted what lies past the next star. The weave of the cosmos unspools only for those who fly into it — so take the helm, and let the dark reveal itself.</p>` +
      `<ul class="sw-ob-controls">${controls}</ul>` +
      `<button type="button" class="sw-ob-begin">Begin<span class="sw-ob-arrow">→</span></button>` +
    `</div>`;

  let finished = false;
  const finish = (): void => {
    if (finished) return;
    finished = true;
    try { localStorage.setItem(SEEN_KEY, '1'); } catch { /* private mode */ }
    scrim.classList.remove('in');
    scrim.classList.add('out');
    const remove = (): void => { scrim.remove(); onDone(); };
    scrim.addEventListener('transitionend', remove, { once: true });
    // Fallback in case the transition is suppressed (reduced motion / no support).
    setTimeout(remove, 800);
  };

  const beginBtn = scrim.querySelector('.sw-ob-begin') as HTMLButtonElement;
  beginBtn.addEventListener('click', finish);
  // Also dismiss on Enter for keyboard users.
  scrim.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') finish();
  });

  root.appendChild(scrim);
  // Fade the scrim + slide the card in on the next frame.
  requestAnimationFrame(() => requestAnimationFrame(() => scrim.classList.add('in')));
  // Focus the button for keyboard/AT users without scrolling the layout.
  setTimeout(() => beginBtn.focus({ preventScroll: true }), 60);
}

/**
 * Mount the onboarding overlay. Call once, after bootDone().
 * On first run shows the intro card, then reveals the persistent control-hint
 * chip. On subsequent runs only the chip is shown (in its saved state).
 */
export function init(): void {
  if (mounted) return;
  mounted = true;

  injectStyle();
  const root = ensureRoot();

  let seen = false;
  try { seen = localStorage.getItem(SEEN_KEY) === '1'; } catch { seen = false; }

  if (seen) {
    buildChip(root);
  } else {
    buildIntro(root, () => buildChip(root));
  }
}

/** Force the first-run intro to show again next launch (e.g. a 'replay intro' setting). */
export function resetOnboarding(): void {
  try {
    localStorage.removeItem(SEEN_KEY);
    localStorage.removeItem(CHIP_KEY);
  } catch { /* private mode */ }
}
